import React from 'react';
import { Crown } from 'lucide-react';
import { useAuthContext } from './contexts/AuthContext';
import { useSettings } from './contexts/SettingsContext';
import { useUpgradeFlow } from './contexts/UpgradeFlowContext';
import { AccountForm } from './components/AccountForm';
import FullscreenApp from './FullscreenApp';
import { InstallAppButton } from './web/InstallAppButton';
import { WebAccountChip } from './web/WebAccountChip';

/**
 * Web/PWA shell (ADR-012). Sign-in required by design: library data lives in
 * Firestore only for signed-in (cloud-sync) accounts, so signed-out visitors
 * get a landing/sign-in screen instead of an empty library, and signed-in
 * free accounts get an upgrade screen instead of an empty library (cloud
 * sync is premium). Premium status is read from settings and kept live by
 * PREMIUM_STATUS_UPDATE bus messages, so this screen swaps to the app by
 * itself the moment the reconcile confirms a subscription — including right
 * after checkout completes in the other tab.
 */
export default function WebApp() {
  const { currentUser, isLoading } = useAuthContext();
  const { settings } = useSettings();

  if (isLoading) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div
          className="animate-spin h-8 w-8 border-2 border-indigo-600 border-t-transparent rounded-full"
          role="status"
          aria-label="Loading"
        />
      </div>
    );
  }

  if (!currentUser) {
    return <WebLanding />;
  }

  const isPremium = settings?.premium_status === 'paid';
  if (!isPremium) {
    return <WebUpgrade />;
  }

  // No account chip here: the settings page (Account tab) handles sign-out,
  // and a fixed bottom-left chip covered the sidebar's Collapse button.
  return (
    <>
      <FullscreenApp />
      <InstallAppButton />
    </>
  );
}

function WebLanding() {
  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <div className="flex justify-center mb-6">
          <img
            src="/icons/TopPromptLibraryHeader.png"
            alt="Top Prompt Library"
            className="h-14 w-auto"
          />
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
          Save, organize, and use your AI prompts anywhere — on the web, installed as a desktop app,
          or in the browser extension. Create an account or sign in to get started; no extension
          needed.
        </p>
        <AccountForm defaultMode="signup" />
        <p className="mt-6 text-xs text-gray-500 dark:text-gray-400 text-center">
          The web app comes with Premium — you can subscribe right here after creating your account.
          Prefer to try it free first? The{' '}
          <a
            href="https://chromewebstore.google.com/detail/top-prompt-library/aonajceephccckeijagkklpaeidkmmol"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-indigo-600 dark:hover:text-indigo-400"
          >
            browser extension
          </a>{' '}
          works without an account.
        </p>
      </div>
      <InstallAppButton />
    </div>
  );
}

/**
 * Signed in but not premium. Cloud sync (which the web app is built on) is a
 * premium feature, so this replaces the empty library free accounts would
 * otherwise see. startUpgrade() opens Stripe Checkout directly — the user is
 * already signed in (ADR-011).
 */
function WebUpgrade() {
  const { startUpgrade } = useUpgradeFlow();

  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center">
            <Crown className="w-6 h-6 text-amber-500" aria-hidden="true" />
          </div>
        </div>
        <h1 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
          One step left: the web app comes with Premium
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Premium powers the cloud sync behind this app. Upgrade to use your prompt library here,
          install it as a desktop app, and keep everything in sync across your devices — the browser
          extension included, if you use it.
        </p>
        <button
          onClick={startUpgrade}
          className="w-full py-2 px-4 rounded-md text-white font-medium bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
        >
          Upgrade to Premium
        </button>
        <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">
          Just upgraded or signed in? This screen updates by itself once your subscription is
          confirmed — it can take a few seconds.
        </p>
      </div>
      <InstallAppButton />
      <WebAccountChip />
    </div>
  );
}
